import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SectionCard from '../../components/SectionCard.jsx';
import Dialog, { DialogError } from '../../components/Dialog.jsx';
import ExternalLink from '../../components/ExternalLink.jsx';
import StatusWord from '../../components/StatusWord.jsx';
import { api } from '../../lib/api.js';
import { CANCEL_REASONS, REMOVAL_STATE, REVIEWER_STATE, daysLeftWords, periodWords, personName, plural, shortDate } from '../../lib/access-review-format.js';
import { REVIEWER_RULE_WORDS, STATE_WORDS } from '../../../../shared/access-reviews.mjs';

export const CURRENT_ID = 'access-reviews-current';
export const REMOVALS_ID = 'access-reviews-removals';

const TH = 'border-b border-ipc-border px-3 py-2 text-left text-[12.5px] font-semibold text-ipc-muted';
const TD = 'border-b border-ipc-border px-3 py-2.5 align-top text-sm text-ipc-ink';

/**
 * "Current campaign: Q4 2026" (spec 13.1, item 5): reviewer progress (reviewer, chosen as, items, decided, last activity,
 * state as a dot and a word), then "Removals to make" with the exact Microsoft Entra link. The viewer and the operator get
 * the same table without names (the API sends none) and the removals as counts.
 */
/** "Review now": a button that moves to the workspace in the page, or a link to the caller's own review page. */
function ReviewNow({ action }) {
  const cls = 'tap text-sm font-semibold text-ipc-light hover:underline';
  if (action?.onClick) return <button type="button" className={cls} onClick={action.onClick}>Review now</button>;
  if (action?.to) return <Link className={cls} to={action.to}>Review now</Link>;
  return null;
}

export default function CurrentCampaign({ current, kpis, customerId, can, onChanged, reviewNow = null }) {
  const [dialog, setDialog] = useState(null);
  const [state, setState] = useState({ busy: false, error: null });
  const closeRef = useRef(null);
  const undecided = Math.max(0, (kpis?.total ?? 0) - (kpis?.decided ?? 0));
  const closable = can.close && ['open', 'overdue'].includes(current.state);
  const cancellable = can.edit && ['open', 'overdue'].includes(current.state);
  const names = can.names;

  const close = async () => {
    setState({ busy: true, error: null });
    try {
      await api.closeAccessReview(customerId, current.campaignId, { confirmUndecided: undecided });
      setDialog(null);
      setState({ busy: false, error: null });
      onChanged();
    } catch (e) {
      setState({ busy: false, error: e?.body?.message ?? e?.message ?? 'The campaign was not closed. Try again.' });
      if (!dialog) setDialog('close-error');
    }
  };
  const onClose = () => { if (undecided > 0) { setState({ busy: false, error: null }); setDialog('close'); } else close(); };

  const period = periodWords(current.openedAt, current.dueAt);
  const left = daysLeftWords(current.daysLeft);
  const description = `${period}. ${STATE_WORDS[current.state] ?? current.state}${left ? `, ${left}` : ''}.`;
  const reviewers = current.reviewers ?? [];
  const removals = current.removals ?? [];

  return (
    <SectionCard
      id={CURRENT_ID} title={`Current campaign: ${current.name}`} description={description} testId="access-reviews-current"
      action={closable ? <button ref={closeRef} type="button" className="btn-secondary min-h-[36px]" onClick={onClose} data-testid="access-reviews-close">Close campaign</button> : null}
    >
      {reviewNow ? <p className="mb-3" data-testid="access-reviews-review-now"><ReviewNow action={reviewNow} /></p> : null}
      {current.startedByStaff ? <p className="mb-3 text-[13px] text-ipc-muted">Started by IPC Global staff on your behalf.</p> : null}
      <div className="-mx-3 overflow-x-auto">
        <table className="w-full border-collapse" data-testid="access-reviews-reviewers">
          <caption className="sr-only">Reviewer progress</caption>
          <thead>
            <tr>
              {names ? <th scope="col" className={TH}>Reviewer</th> : null}
              <th scope="col" className={names ? `${TH} hidden sm:table-cell` : TH}>Chosen as</th>
              <th scope="col" className={`${TH} text-right`}>Items</th>
              <th scope="col" className={`${TH} text-right`}>Decided</th>
              <th scope="col" className={`${TH} hidden sm:table-cell`}>Last activity</th>
              <th scope="col" className={TH}>State</th>
            </tr>
          </thead>
          <tbody>
            {reviewers.map((r) => {
              const st = REVIEWER_STATE[r.state] ?? REVIEWER_STATE['not-started'];
              const pending = r.items - r.decided;
              return (
                <tr key={r.reviewerRef}>
                  {names ? <td className={`${TD} font-semibold`}>{personName({ displayName: r.displayName }, 'Reviewer')}</td> : null}
                  <td className={names ? `${TD} hidden sm:table-cell` : TD}>{REVIEWER_RULE_WORDS[r.rule] ?? r.rule}</td>
                  <td className={`${TD} text-right tabular-nums`}>{r.items}</td>
                  <td className={`${TD} text-right tabular-nums`}>{r.decided}</td>
                  <td className={`${TD} hidden sm:table-cell`}>{shortDate(r.lastActivityAt) || 'None yet'}</td>
                  <td className={TD}>
                    <StatusWord tone={st.tone}>{r.state === 'escalated' && pending > 0 ? `${st.word}, ${pending}` : st.word}</StatusWord>
                    {r.you === true && pending > 0 && reviewNow ? <span className="ml-3"><ReviewNow action={reviewNow} /></span> : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {current.addedSince > 0 ? (
        <p className="mt-3 text-[13.5px] text-ipc-muted" data-testid="access-reviews-added-since">
          Added since this campaign started: {plural(current.addedSince, 'assignment')}. They join the next campaign.
        </p>
      ) : null}

      <div id={REMOVALS_ID} className="mt-6" data-testid="access-reviews-removals">
        <div className="mb-2 flex flex-wrap items-baseline gap-x-3">
          <h3>Removals to make ({removals.filter((x) => x.state !== 'verified-removed').length})</h3>
          <p className="text-[13.5px] text-ipc-muted">Remove these in Microsoft Entra; onGuard verifies on the next collection.</p>
        </div>
        {removals.length === 0 ? <p className="text-sm text-ipc-muted">No removal decided yet.</p> : null}
        {removals.length > 0 && !names ? (
          <p className="text-sm text-ipc-ink" data-testid="access-reviews-removals-counts">
            {plural(removals.length, 'removal')} decided, {removals.filter((x) => x.state === 'verified-removed').length} verified removed,{' '}
            {removals.filter((x) => x.state === 'overdue').length} past the removal window.
          </p>
        ) : null}
        {removals.length > 0 && names ? (
          <div className="-mx-3 overflow-x-auto">
            <table className="w-full border-collapse">
              <caption className="sr-only">Removals to make</caption>
              <thead>
                <tr>
                  <th scope="col" className={TH}>Holder</th>
                  <th scope="col" className={TH}>Role</th>
                  <th scope="col" className={`${TH} hidden md:table-cell`}>Decided by</th>
                  <th scope="col" className={TH}>State</th>
                  <th scope="col" className={TH}><span className="sr-only">Action</span></th>
                </tr>
              </thead>
              <tbody>
                {removals.map((r) => {
                  const st = REMOVAL_STATE[r.state] ?? REMOVAL_STATE.pending;
                  const word = r.state === 'verified-removed' && r.verifiedAt ? `${st.word} ${shortDate(r.verifiedAt)}` : r.state === 'overdue' ? `${st.word}, ${plural(r.days ?? 0, 'day')}` : st.word;
                  return (
                    <tr key={r.itemRef}>
                      <td className={TD}>
                        <span className="block font-semibold">{personName(r.holder, 'Holder')}</span>
                        {r.holder?.upn ? <span className="block max-w-[14rem] truncate text-[12.5px] text-ipc-muted" title={r.holder.upn}>{r.holder.upn}</span> : null}
                      </td>
                      <td className={TD}>{r.role}</td>
                      <td className={`${TD} hidden md:table-cell`}>
                        <span className="block whitespace-nowrap">{r.decidedBy ?? 'Reviewer'}</span>
                        {r.decidedAt ? <span className="block text-[12.5px] text-ipc-muted">{shortDate(r.decidedAt)}</span> : null}
                      </td>
                      <td className={`${TD} whitespace-nowrap`}><StatusWord tone={st.tone} strong={false}>{word}</StatusWord></td>
                      <td className={`${TD} text-right`}>
                        {r.state !== 'verified-removed' && r.entraUrl ? <ExternalLink href={r.entraUrl} className="whitespace-nowrap font-semibold text-ipc-light hover:underline">Open in Microsoft Entra</ExternalLink> : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {cancellable ? (
        <div className="mt-5 border-t border-ipc-border pt-3 text-[13.5px]">
          <button type="button" className="tap font-semibold text-ipc-light hover:underline" onClick={() => { setState({ busy: false, error: null }); setDialog('cancel'); }} data-testid="access-reviews-cancel">Cancel this campaign</button>
        </div>
      ) : null}

      <Dialog
        open={dialog === 'close' || dialog === 'close-error'} onClose={() => setDialog(null)} returnFocusRef={closeRef}
        title={dialog === 'close' ? `Close with ${undecided} undecided?` : 'The campaign was not closed'}
        footer={(
          <>
            <button type="button" className="btn-secondary" onClick={() => setDialog(null)}>Keep it open</button>
            <button type="button" className="btn-primary" disabled={state.busy} onClick={close} data-testid="access-reviews-close-confirm">{state.busy ? 'Closing' : 'Close campaign'}</button>
          </>
        )}
      >
        {dialog === 'close' ? <p data-testid="access-reviews-close-question">They are recorded as No decision in the evidence. Closing cannot be undone. The results report is created at once; removals still waiting are verified on the next collections and recorded in a verification addendum.</p> : null}
        <DialogError message={state.error} />
      </Dialog>
      <CancelDialog
        open={dialog === 'cancel'} onClose={() => setDialog(null)} customerId={customerId} campaignId={current.campaignId}
        onDone={() => { setDialog(null); onChanged(); }}
      />
    </SectionCard>
  );
}

function CancelDialog({ open, onClose, customerId, campaignId, onDone }) {
  const [reason, setReason] = useState('');
  const [state, setState] = useState({ busy: false, error: null });
  const submit = async () => {
    if (!reason) { setState({ busy: false, error: 'Pick why you cancel the campaign.' }); return; }
    setState({ busy: true, error: null });
    try {
      await api.cancelAccessReview(customerId, campaignId, { reason });
      setState({ busy: false, error: null });
      onDone();
    } catch (e) {
      setState({ busy: false, error: e?.body?.message ?? e?.message ?? 'The campaign was not cancelled. Try again.' });
    }
  };
  return (
    <Dialog
      open={open} onClose={onClose} title="Cancel this campaign?"
      description="Reviewers can no longer decide. The campaign stays in the list and in the evidence as cancelled, with your reason."
      footer={(
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>Keep it open</button>
          <button type="button" className="btn-primary" disabled={state.busy} onClick={submit} data-testid="access-reviews-cancel-confirm">{state.busy ? 'Cancelling' : 'Cancel campaign'}</button>
        </>
      )}
    >
      <fieldset>
        <legend className="font-semibold">Why</legend>
        <div className="mt-2 space-y-1.5">
          {CANCEL_REASONS.map((r) => (
            <label key={r.id} className="flex items-center gap-2">
              <input type="radio" name="cancel-reason" value={r.id} checked={reason === r.id} onChange={() => setReason(r.id)} />
              {r.label}
            </label>
          ))}
        </div>
      </fieldset>
      <DialogError message={state.error} />
    </Dialog>
  );
}
